/**
 * SkeletonText — placeholder for text content.
 *
 * Renders `lines` shimmer bars at body-text height.
 * Uses only CSS custom-property references.
 *
 * @see specs/integration-plan.md Section 5.1
 */
import React from "react";
import { useShimmer } from "./useShimmer";

const SKELETON_ANIMATION = "skel 1.2s ease-in-out infinite";

const SKELETON_GRADIENT =
  "linear-gradient(90deg, var(--bg2) 0%, rgba(0,0,0,0.06) 50%, var(--bg2) 100%)";

export interface SkeletonTextProps {
  /** Number of placeholder lines. Default: 1. */
  lines?: number;
  /** Width of the skeleton container. Default: "100%". */
  width?: string | number;
}

const LINE_HEIGHT = "var(--fs-body-lg, 16px)";
const LINE_GAP = "var(--space-xs, 6px)";

const shimmerBackground =
  SKELETON_GRADIENT;

export function SkeletonText({
  lines = 1,
  width = "100%",
}: SkeletonTextProps): React.ReactElement {
  const animate = useShimmer();
  const count = Math.max(0, lines);

  return (
    <div
      data-testid="skeleton-text"
      aria-hidden="true"
      style={{ width: typeof width === "number" ? `${width}px` : width }}
    >
      {Array.from({ length: count }, (_, i) => (
        <div
          key={i}
          style={{
            height: LINE_HEIGHT,
            marginTop: i > 0 ? LINE_GAP : undefined,
            borderRadius: "var(--radius-sm, 4px)",
            background: shimmerBackground,
            backgroundSize: "200% 100%",
            animation: animate
              ? SKELETON_ANIMATION
              : "none",
          }}
        />
      ))}
    </div>
  );
}
